const HINT_CLIENT_TOKEN_KEY = 'spence-hint-token'

const dropAHintForm = class extends HTMLElement {
  constructor() {
    super()

    this.endpoint = this.dataset.endpoint || '/apps/spence/send-hint'
    this.productUrl = this.dataset.productUrl || ''
    this.productFormId = this.dataset.productFormId || ''
    this.defaultImage = this.dataset.defaultImage || ''

    this.form = this.querySelector('[data-drop-a-hint-form]')
    this.senderNameField = this.querySelector('input[name="sender_name"]')
    this.partnerNameField = this.querySelector('input[name="partner_name"]')
    this.partnerEmailField = this.querySelector('input[name="partner_email"]')
    this.productImageField = this.querySelector('input[name="product_image"]')
    this.productUrlField = this.querySelector('input[name="product_url"]')
    this.clientTokenField = this.querySelector('input[name="client_token"]')
    this.honeypotField = this.querySelector('input[name="contact_reason"]')
    this.fields = this.querySelector('.drop-a-hint-form__fields')
    this.messages = this.querySelector('.drop-a-hint-form__messages')
    this.success = this.querySelector('.drop-a-hint-form__success')
    this.error = this.querySelector('.drop-a-hint-form__error')
    this.errorMessage = this.querySelector('[data-error-message]')
    this.submitButton = this.querySelector('button[type="submit"]')
    this.dialog = this.closest('x-modal, x-drawer')

    this.copy = this.parseMessages()
    this.onVariantChange = this.onVariantChange.bind(this)

    // Set when the hint is opened for something other than the plain product page,
    // e.g. a ring the shopper configured in the customizer.
    this.urlOverride = null
    this.imageOverride = null

    if (this.clientTokenField) {
      this.clientTokenField.value = this.clientToken()
    }

    this.form && this.form.addEventListener('submit', (e) => {
      e.preventDefault()

      if (!this.form.checkValidity()) {
        const invalidField = this.form.querySelector(':invalid') || this.partnerEmailField
        invalidField.reportValidity()
        return
      }

      this.clearMessages()
      this.submitForm()
    })

    this.dialog?.addEventListener('dialog:before-hide', () => {
      this.submitButton?.removeAttribute('aria-busy')
    })

    this.dialog?.addEventListener('dialog:after-hide', () => {
      this.form?.reset()
      this.urlOverride = null
      this.imageOverride = null
      this.clearMessages()
      this.fields?.classList.remove('hidden')
      this.submitButton?.removeAttribute('aria-busy')

      if (this.clientTokenField) {
        this.clientTokenField.value = this.clientToken()
      }
    })
  }

  connectedCallback() {
    // The modal is appended to the body, so the variant event is caught on the document instead of the product form.
    document.addEventListener('variant:change', this.onVariantChange)
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange)
  }

  parseMessages() {
    try {
      return JSON.parse(this.querySelector('script[data-messages]')?.textContent || '{}')
    } catch {
      return {}
    }
  }

  clientToken() {
    try {
      let token = sessionStorage.getItem(HINT_CLIENT_TOKEN_KEY)

      if (!token) {
        token = crypto.randomUUID()
        sessionStorage.setItem(HINT_CLIENT_TOKEN_KEY, token)
      }

      return token
    } catch {
      return ''
    }
  }

  /**
   * Keeps the hidden product fields in sync with the selected variant. Events from another product's
   * variant picker are ignored, so a featured product elsewhere on the page cannot hijack the hint.
   */
  onVariantChange(event) {
    if (this.productFormId && event.target?.getAttribute?.('id') !== this.productFormId) return

    const variant = event.detail?.variant

    if (!variant?.id || !this.productUrl) return

    // An overridden URL points at something the variant picker does not describe, so it stands.
    if (this.productUrlField && !this.urlOverride) {
      this.productUrlField.value = `${this.productUrl}?variant=${variant.id}`
    }

    if (this.productImageField && !this.imageOverride) {
      this.productImageField.value = this.imageUrlFor(variant)
    }
  }

  /**
   * Opens the hint dialog, optionally for a URL other than the product page — the
   * customizer uses this to hint at a configured ring. The override survives the
   * variant picker and is dropped when the dialog closes.
   */
  openWith({ productUrl, productImage } = {}) {
    if (productUrl) {
      this.urlOverride = productUrl

      if (this.productUrlField) {
        this.productUrlField.value = productUrl
      }
    }

    if (productImage) {
      this.imageOverride = productImage

      if (this.productImageField) {
        this.productImageField.value = productImage
      }
    }

    this.dialog?.show?.()
  }

  imageUrlFor(variant) {
    const src = variant.featured_image?.src

    if (!src) return this.defaultImage

    try {
      // The proxy rejects protocol-relative URLs, and the variant JSON returns them.
      const url = new URL(src, window.location.origin)
      url.searchParams.set('width', '1200')
      return url.href
    } catch {
      return this.defaultImage
    }
  }

  async clearButtonLoading() {
    this.submitButton?.removeAttribute('aria-busy')
    await new Promise((resolve) => setTimeout(resolve, 200))
  }

  async submitForm() {
    this.submitButton?.setAttribute('aria-busy', 'true')

    const body = new URLSearchParams({
      sender_name: this.senderNameField?.value || '',
      partner_name: this.partnerNameField?.value || '',
      partner_email: this.partnerEmailField?.value || '',
      product_image: this.productImageField?.value || '',
      product_url: this.productUrlField?.value || '',
      client_token: this.clientTokenField?.value || '',
      contact_reason: this.honeypotField?.value || ''
    })

    let response
    let payload

    try {
      response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json'
        },
        body
      })

      payload = await response.json()
    } catch {
      // Never log the request body: the recipient address must not be retained anywhere.
      console.warn('Drop a hint: no JSON response from', this.endpoint, response ? `(status ${response.status})` : '(network error)')
      await this.clearButtonLoading()
      this.showError()
      return
    }

    await this.clearButtonLoading()

    if (payload?.ok) {
      this.showSuccess()
      this.dispatchEvent(new CustomEvent('submit_success', { detail: response }))
      return
    }

    this.handleFailure(response, payload)
  }

  handleFailure(response, payload) {
    if (payload?.code === 'invalid_input') {
      this.showFieldErrors(payload.fields)
      return
    }

    if (payload?.code === 'rate_limited') {
      const retryAfter = parseInt(response.headers.get('Retry-After'), 10)

      if (retryAfter > 0 && this.copy.rate_limited_seconds) {
        this.showError(this.copy.rate_limited_seconds.replace('[seconds]', retryAfter))
        return
      }

      this.showError(this.copy.rate_limited)
      return
    }

    this.showError()
  }

  showFieldErrors(fields) {
    const entries = Array.isArray(fields) ? fields : []
    let shownInline = false

    entries.forEach(({ field, code }) => {
      const input = this.querySelector(`input[name="${field}"]`)
      const target = this.querySelector(`[data-error-for="${field}"]`)

      if (!input || !target) return

      target.textContent = this.copy[code] || this.copy.invalid_input
      target.classList.remove('hidden')
      input.setAttribute('aria-invalid', 'true')
      if (target.id) input.setAttribute('aria-describedby', target.id)
      shownInline = true
    })

    // The hidden product fields have no inline slot, and a stale product URL is only fixed by a refresh.
    if (!shownInline) {
      this.showError(entries.length ? this.copy.product_stale : this.copy.invalid_input)
      return
    }

    this.showError(this.copy.invalid_input)
  }

  clearMessages() {
    this.messages?.classList.add('hidden')
    this.success?.classList.add('hidden')
    this.error?.classList.add('hidden')

    this.querySelectorAll('[data-error-for]').forEach((element) => {
      element.textContent = ''
      element.classList.add('hidden')
    })

    this.querySelectorAll('[aria-invalid]').forEach((element) => {
      element.removeAttribute('aria-invalid')
    })
  }

  showSuccess() {
    this.messages?.classList.remove('hidden')
    this.success?.classList.remove('hidden')
    this.error?.classList.add('hidden')
    this.fields?.classList.add('hidden')
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message || this.copy.generic || this.errorMessage.textContent
    }

    this.messages?.classList.remove('hidden')
    this.error?.classList.remove('hidden')
    this.success?.classList.add('hidden')
  }
}

if (typeof window.customElements.get('drop-a-hint-form') === 'undefined') {
  window.customElements.define('drop-a-hint-form', dropAHintForm)

  /*
   * Opens the hint dialog from anywhere, the way filter tooltips are opened:
   *
   *   document.dispatchEvent(new CustomEvent('drop-a-hint:open'))
   *   document.dispatchEvent(new CustomEvent('drop-a-hint:open', { detail: { productUrl: '...' } }))
   *
   * productUrl and productImage replace what the hint would otherwise send for the
   * product. Both survive the variant picker and are dropped when the dialog closes.
   * Pass an id to target one placement when a page renders more than one hint form.
   */
  document.addEventListener('drop-a-hint:open', (event) => {
    const { id, productUrl, productImage } = event.detail ?? {}
    const scope = id ? document.getElementById(id) : document
    const form = scope?.querySelector('drop-a-hint-form')

    if (!form?.openWith) {
      console.warn('Drop a hint: no hint form on this page to open')
      return
    }

    form.openWith({ productUrl, productImage })
  })
}
